import React, { useState } from 'react';

import Checkbox from '@amzn/meridian/checkbox';
import Column from '@amzn/meridian/column';
import Input from '@amzn/meridian/input';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import { useQuery } from '@tanstack/react-query';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import { type ErrorRecord, type HandleChange } from 'src/hooks/useForm';
import { getSiteQueryOptions } from 'src/services/sites/siteQueries';
import { type NonPhysicalTaskDTO } from 'src/services/tasks/types';
import { getAccommodationTypesOptions } from 'src/utils/accommodationTypes';
import { createSearchRegExp } from 'src/utils/searchUtils';

interface NonPhysicalTaskFormFieldProps {
  formData: NonPhysicalTaskDTO;
  errors: ErrorRecord<NonPhysicalTaskDTO>;
  handleChange: HandleChange<NonPhysicalTaskDTO>;
}

const NonPhysicalTaskFormFields = ({ formData, errors, handleChange }: NonPhysicalTaskFormFieldProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { data: regions = [], isLoading } = useQuery(getSiteQueryOptions.regions());
  const searchRegExp = createSearchRegExp(searchQuery);
  const matchedRegionOptions = regions
    .filter((region) => !searchQuery || searchRegExp.test(region.name))
    .map((region) => ({ label: region.name, value: region.id }));

  const selectedCountry = regions.find((r) => r.id === formData.regionId)?.country;
  const accommodationTypeSelectOptions = !!selectedCountry
    ? getAccommodationTypesOptions(selectedCountry.id, true)
    : [];

  return (
    <Column>
      <Row widths="fill">
        <Input
          label="Task Name*"
          type="text"
          placeholder="Enter task name..."
          aria-label="Task Name"
          value={formData.name}
          onChange={(v) => handleChange({ name: v })}
          error={errors.name?.error}
          errorMessage={errors.name?.errorMessage}
        />
      </Row>
      <Row widths="fill">
        <Select
          label="Region/State/Province*"
          searchQuery={searchQuery}
          onSearch={setSearchQuery}
          aria-label="Select Region"
          placeholder="Select region..."
          value={formData.regionId}
          onChange={(regionId) => {
            handleChange({ regionId });
          }}
          error={errors.regionId?.error}
          errorMessage={errors.regionId?.errorMessage}
        >
          {isLoading && <LoadingSpinner />}
          {matchedRegionOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>
      <Row widths="fill">
        <Input
          label="Description"
          type="text"
          placeholder="Enter description..."
          aria-label="Description"
          value={formData.description}
          onChange={(v) => handleChange({ description: v })}
          error={errors.description?.error}
          errorMessage={errors.description?.errorMessage}
        />
      </Row>

      <Row widths="fill">
        <Select
          label="Accommodation Types*"
          aria-label="Select Accommodation Type"
          disabled={!accommodationTypeSelectOptions.length}
          placeholder={
            formData.regionId
              ? 'Select an accommodation type (e.g. AUSTIN)'
              : 'Select a region to see available accommodation types'
          }
          value={formData.accommodationTypeId}
          onChange={(accommodationTypeId) => handleChange({ accommodationTypeId })}
          error={errors.accommodationTypeId?.error}
          errorMessage={errors.accommodationTypeId?.errorMessage}
        >
          {accommodationTypeSelectOptions.map((option) => (
            <SelectOption label={option.label} value={option.value} key={option.value} />
          ))}
        </Select>
      </Row>
      <Row alignmentVertical="center" widths="fill">
        <Checkbox
          size="medium"
          aria-label="Allow Frequency"
          checked={formData.allowsFrequency}
          onChange={(allowsFrequency) => handleChange({ allowsFrequency })}
        >
          Allow Frequency?
        </Checkbox>
      </Row>
      <Row alignmentVertical="center" widths="fill">
        <Checkbox
          size="medium"
          aria-label="Allow Duration"
          checked={formData.allowsDuration}
          onChange={(allowsDuration) => handleChange({ allowsDuration })}
        >
          Allow Duration?
        </Checkbox>
      </Row>
      <Row alignmentVertical="center" widths="fill">
        <Checkbox
          size="medium"
          aria-label="Allow Free Form Text"
          checked={formData.allowsFreeFormText}
          onChange={(allowsFreeFormText) => handleChange({ allowsFreeFormText })}
        >
          Allow Free Form Text?
        </Checkbox>
      </Row>
    </Column>
  );
};

export default NonPhysicalTaskFormFields;
